import { FlowType } from '@core/enums/flow-type.enum';
import { PageKey } from '@core/enums/page-keys.enum';
import { VisitorType } from '@core/enums/visitor-type.enum';
import { FlowId, type CheckinWorkflow, type WorkflowVariables } from '../workflow-engine.types';
import { covidDeclarationFlow } from './covid-declaration.flow';
import { dailyCheckinCheckoutFlow } from './daily-checkin-checkout.flow';
import { existingQrCodeFlow } from './existing-qr-code.flow';
import { manualFlow } from './manual-flow.flow';
import { reprintFlow } from './reprint.flow';

export enum CheckinProcessStep {
  Welcome = 'Welcome',
  ReasonForVisit = 'ReasonForVisit',
  CheckinType = 'CheckinType',
  ReasonFlowSelection = 'ReasonFlowSelection',
  CheckinTypeFlowSelection = 'CheckinTypeFlowSelection',
  DailyCheckinCheckout = 'DailyCheckinCheckout',
  Complete = 'Complete',
}

function requiresCheckinType(variables: WorkflowVariables): boolean {
  return (
    variables.visitorType === VisitorType.Trainee &&
    variables.checkinProcessConfiguration?.hasTraineeFlow === true &&
    variables.checkinProcessConfiguration.hasIHaveAQrCodeFlow === true
  );
}

export const checkinProcessFlow: CheckinWorkflow<CheckinProcessStep> = {
  id: FlowId.CheckinProcess,
  entry: CheckinProcessStep.Welcome,
  steps: {
    [CheckinProcessStep.Welcome]: {
      id: CheckinProcessStep.Welcome,
      page: PageKey.WELCOME,
      next: () => ({ nextStep: CheckinProcessStep.ReasonForVisit }),
    },
    [CheckinProcessStep.ReasonForVisit]: {
      id: CheckinProcessStep.ReasonForVisit,
      page: PageKey.REASON_VISIT,
      next: (variables) => {
        if (variables.flowType === FlowType.COVID_DECLARATION) {
          return { nextStep: CheckinProcessStep.ReasonFlowSelection };
        }
        if (variables.flowType === FlowType.REPRINT_BADGE) {
          return { nextStep: CheckinProcessStep.ReasonFlowSelection };
        }
        if (variables.flowType === FlowType.DAILY_CHECKIN_CHECKOUT) {
          return { nextStep: CheckinProcessStep.DailyCheckinCheckout };
        }
        return requiresCheckinType(variables)
          ? { nextStep: CheckinProcessStep.CheckinType }
          : { nextStep: CheckinProcessStep.ReasonFlowSelection };
      },
      back: () => ({ nextStep: CheckinProcessStep.Welcome }),
      exit: () => ({ complete: true }),
    },
    [CheckinProcessStep.CheckinType]: {
      id: CheckinProcessStep.CheckinType,
      page: PageKey.CHECKIN_TYPE,
      next: () => ({ nextStep: CheckinProcessStep.CheckinTypeFlowSelection }),
      back: () => ({ nextStep: CheckinProcessStep.ReasonForVisit }),
      exit: () => ({ complete: true }),
    },
    [CheckinProcessStep.ReasonFlowSelection]: {
      id: CheckinProcessStep.ReasonFlowSelection,
      next: (variables) => {
        if (variables.flowType === FlowType.COVID_DECLARATION) {
          return { flow: covidDeclarationFlow, returnStep: CheckinProcessStep.Complete };
        }
        if (variables.flowType === FlowType.REPRINT_BADGE) {
          return { flow: reprintFlow, returnStep: CheckinProcessStep.Complete };
        }
        return { flow: manualFlow, returnStep: CheckinProcessStep.Complete };
      },
      back: () => ({ nextStep: CheckinProcessStep.ReasonForVisit }),
      exit: () => ({ complete: true }),
    },
    [CheckinProcessStep.CheckinTypeFlowSelection]: {
      id: CheckinProcessStep.CheckinTypeFlowSelection,
      next: (variables) => {
        if (variables.flowType === FlowType.MANUAL) {
          return { flow: manualFlow, returnStep: CheckinProcessStep.Complete };
        }
        if (variables.flowType === FlowType.EXISTING_QRCODE) {
          return { flow: existingQrCodeFlow, returnStep: CheckinProcessStep.Complete };
        }
        throw new Error(`Unsupported check-in type: ${variables.flowType}`);
      },
      back: () => ({ nextStep: CheckinProcessStep.CheckinType }),
      exit: () => ({ complete: true }),
    },
    [CheckinProcessStep.DailyCheckinCheckout]: {
      id: CheckinProcessStep.DailyCheckinCheckout,
      next: () => ({ flow: dailyCheckinCheckoutFlow, returnStep: CheckinProcessStep.Complete }),
      back: () => ({ nextStep: CheckinProcessStep.ReasonForVisit }),
      exit: () => ({ complete: true }),
    },
    [CheckinProcessStep.Complete]: {
      id: CheckinProcessStep.Complete,
      next: () => ({ complete: true }),
    },
  },
};
